import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
const BASE = `${API_URL}/admin/weekly-mail`;

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
});

/**
 * How many days before its send a brief is prepared — `PREVIEW_LEAD_DAYS` server-side.
 *
 * Six, not two: the commercial back-office builds its calling list out of the prepared brief,
 * so this is also the window the sales team has to phone the establishments before the client
 * reads about them. Kept here rather than typed into each sentence that mentions it, because
 * three hand-written "2 jours" are exactly what went stale when the value moved.
 */
export const PREVIEW_LEAD_DAYS = 6;

/** Plancher d'établissements par mail — `MIN_ESTABLISHMENTS` côté serveur. */
export const MIN_ESTABLISHMENTS = 5;

// ─── Filters & establishments ─────────────────────────────────────────────────

export type WeeklyResendBlockReason = "recipient" | "cross_client" | "pending_draft";

export interface WeeklyMailSlots {
    /** 1–2 restaurants inspected the day before (proof the place is trading) */
    hygiene: boolean;
    /** one owner running 2 to 10 establishments — "compte à gros potentiel" */
    multiOwner: boolean;
    /** LinkedIn + email + phone + Instagram — "fort potentiel de réactivité" */
    richContacts: boolean;
}

export interface WeeklyMailFilters {
    cities: string[];
    regions: string[];
    departments: string[];
    /** NAF codes — replaces the former "types d'établissement" filter */
    nafCodes: string[];
    signalTypes: string[];
    categories: string[];
    subCategories: string[];
    dateFrom: string;
    dateTo: string;
    includeIds?: string[];
    manualIds?: string[];
    customTitle?: string;
    maxEstablishments?: number;
    slots?: WeeklyMailSlots;
    /** destinataires visés — pilote le blocage de 6 mois côté serveur */
    recipientEmails?: string[];
}

export interface WeeklyOwner {
    name: string;
    age: number | null;
    establishmentCount: number;
}

export interface WeeklyEstablishment {
    _id: string;
    name: string;
    type: string | null;
    siret?: string | null;
    siren?: string | null;
    /** page Google Maps de l'établissement, quand la fiche en porte une */
    googleUrl?: string | null;
    /** dernier envoi de cet établissement, au sens du blocage qui s'applique */
    lastSentAt?: string | null;
    /** date à laquelle il redevient envoyable */
    resendBlockedUntil?: string | null;
    /** recherche par typologie : l'étape d'élargissement qui l'a trouvé (absent = correspondance stricte) */
    relaxed?: string | null;
    /**
     * Quel blocage s'applique :
     * `recipient` — déjà envoyé à ce destinataire (6 mois) ·
     * `cross_client` — envoyé à un autre client (3 semaines) ·
     * `pending_draft` — déjà dans un autre envoi en préparation
     */
    resendBlockReason?: WeeklyResendBlockReason | null;
    address: {
        street?: string;
        postcode?: string;
        city?: string;
        region?: string;
    } | null;
    phone: string | null;
    email: string | null;
    brand_group: string | null;
    enseigne: string | null;
    nafCode: string | null;
    nafLabel: string | null;
    googleRating: number | null;
    googleReviews: number | null;
    owner: WeeklyOwner | null;
    categories: string[];
    subCategories: string[];
    signals: { type: string; label: string; date: string }[];
}

export type WeeklySlotKind = "manual" | "signal" | "hygiene" | "multi_owner" | "rich_contacts" | "backfill";

export interface WeeklySelectionItem {
    _id: string;
    name: string;
    slot: WeeklySlotKind;
    slotLabel: string;
    reason: string;
}

export interface WeeklyMailResult {
    html: string;
    subject: string;
    preheader: string;
    selection: WeeklySelectionItem[];
    stats: {
        establishments: number;
        news: number;
        totalSignals: number;
        excluded: number;
        /** candidats trouvés puis retenus par le blocage de 6 mois */
        blocked: number;
    };
}

export const previewEstablishments = async (
    filters: WeeklyMailFilters
): Promise<{ data: WeeklyEstablishment[]; total: number; excluded: number }> => {
    const res = await axios.post(`${BASE}/establishments`, filters, { headers: authHeaders() });
    return res.data;
};

/**
 * `draftId` / `profileId` sont facultatifs mais changent la réponse : avec eux, chaque ligne
 * indique si elle a déjà été envoyée aux destinataires visés, et jusqu'à quand elle est bloquée.
 */
export const searchEstablishments = async (
    q: string,
    context?: { draftId?: string; profileId?: string }
): Promise<WeeklyEstablishment[]> => {
    const res = await axios.get(`${BASE}/search`, {
        params: { q, ...context },
        headers: authHeaders(),
    });
    return res.data;
};

/** Résout des SIRET (14 chiffres) ou SIREN (9 chiffres) collés en vrac. */
export const findEstablishmentsBySiret = async (
    codes: string,
    context?: { draftId?: string; profileId?: string }
): Promise<{ found: WeeklyEstablishment[]; missing: string[] }> => {
    const res = await axios.post(
        `${BASE}/establishments/by-siret`,
        { codes, ...context },
        { headers: authHeaders() }
    );
    return res.data;
};

export const getEstablishmentsByIds = async (ids: string[]): Promise<WeeklyEstablishment[]> => {
    if (ids.length === 0) return [];
    const res = await axios.post(`${BASE}/establishments/by-ids`, { ids }, { headers: authHeaders() });
    return res.data;
};

export const generateWeeklyMail = async (filters: WeeklyMailFilters): Promise<WeeklyMailResult> => {
    const res = await axios.post(`${BASE}/generate`, filters, { headers: authHeaders() });
    return res.data;
};

// ─── Mailing profiles ─────────────────────────────────────────────────────────

export interface WeeklyMailRecipient {
    email: string;
    name?: string;
}

export interface WeeklyMailTimeSlot {
    /** 0 = dimanche … 6 = samedi */
    dayOfWeek: number;
    hour: number;
    minute: number;
}

export interface WeeklyMailSchedule {
    /** ancien créneau unique — recopié depuis `times[0]` */
    dayOfWeek: number;
    hour: number;
    minute: number;
    /** tous les créneaux hebdomadaires ; vide sur un profil enregistré avant leur arrivée */
    times?: WeeklyMailTimeSlot[];
    /** null = envois illimités (hors envois ponctuels) */
    occurrences: number | null;
    sentCount: number;
}

export interface WeeklyMailProfileFilters {
    cities: string[];
    regions: string[];
    departments: string[];
    nafCodes: string[];
    signalTypes: string[];
    categories: string[];
    subCategories: string[];
    /** période couverte par chaque envoi : les N jours qui le précèdent */
    lookbackDays: number;
    maxEstablishments: number;
    slots: WeeklyMailSlots;
    manualIds: string[];
}

export interface WeeklyMailProfile {
    _id: string;
    name: string;
    description?: string;
    active: boolean;
    customTitle: string;
    recipients: WeeklyMailRecipient[];
    schedule: WeeklyMailSchedule;
    filters: WeeklyMailProfileFilters;
    nextSendAt: string | null;
    lastSentAt: string | null;
    createdAt?: string;
    upcomingDraft?: {
        _id: string;
        scheduledFor: string;
        status: WeeklyMailDraftStatus;
        edited: boolean;
        cardCount: number;
    } | null;
}

export type WeeklyMailDraftStatus = "draft" | "approved" | "sending" | "sent" | "cancelled" | "failed";

export interface WeeklyMailDraftCard {
    establishmentId: string;
    name: string;
    subtitle: string;
    address: string | null;
    geo: { lat: number; lon: number } | null;
    owner: string | null;
    /** nom seul du dirigeant — le lien vers sa page dans l'app */
    ownerName?: string | null;
    rating: string | null;
    /** page Google Maps de l'établissement — bouton « Fiche Google » de la carte */
    googleUrl: string | null;
    reasonTag: string | null;
    reason: string;
    phone: string | null;
    email: string | null;
    slot: WeeklySlotKind;
}

export interface WeeklyMailDraft {
    _id: string;
    profileId: string;
    scheduledFor: string;
    /** `series` = occurrence du rythme hebdomadaire ; `oneoff` = envoi ponctuel */
    kind?: "series" | "oneoff";
    originalAt?: string | null;
    status: WeeklyMailDraftStatus;
    subject: string;
    preheader: string;
    weekLabel: string;
    customTitle: string;
    cards: WeeklyMailDraftCard[];
    press: { text: string; meta: string; url: string | null }[];
    recipients: WeeklyMailRecipient[];
    stats?: Record<string, number>;
    edited: boolean;
    generatedAt: string;
    sentAt?: string | null;
    error?: string | null;
    /** rendu à la volée par l'API à partir des champs ci-dessus */
    html: string;
    /** jetons signés par établissement, pour ouvrir la page d'avis depuis le BO */
    feedbackTokens?: Record<string, string>;
}

/** URL de la page d'avis publique, sur l'API que ce front interroge. */
export const feedbackPageUrl = (token: string, vote: "relevant" | "not_relevant" = "relevant") =>
    `${API_URL}/hebdo/avis?t=${encodeURIComponent(token)}&v=${vote}`;

export const listProfiles = async (): Promise<WeeklyMailProfile[]> => {
    const res = await axios.get(`${BASE}/profiles`, { headers: authHeaders() });
    return res.data;
};

export const getProfile = async (
    id: string
): Promise<{
    profile: WeeklyMailProfile;
    drafts: WeeklyMailDraft[];
    /** le brief préparé pour le prochain créneau, déjà rendu — null s'il n'y en a pas encore */
    upcomingDraft: WeeklyMailDraft | null;
}> => {
    const res = await axios.get(`${BASE}/profiles/${id}`, { headers: authHeaders() });
    return res.data;
};

export const createProfile = async (payload: Partial<WeeklyMailProfile>): Promise<WeeklyMailProfile> => {
    const res = await axios.post(`${BASE}/profiles`, payload, { headers: authHeaders() });
    return res.data;
};

export const updateProfile = async (
    id: string,
    payload: Partial<WeeklyMailProfile>
): Promise<WeeklyMailProfile> => {
    const res = await axios.patch(`${BASE}/profiles/${id}`, payload, { headers: authHeaders() });
    return res.data;
};

export const deleteProfile = async (id: string): Promise<{ ok: boolean }> => {
    const res = await axios.delete(`${BASE}/profiles/${id}`, { headers: authHeaders() });
    return res.data;
};

/** Prépare (ou régénère avec force) l'aperçu du prochain envoi. */
export const prepareDraft = async (profileId: string, force = false): Promise<WeeklyMailDraft> => {
    const res = await axios.post(`${BASE}/profiles/${profileId}/draft`, { force }, { headers: authHeaders() });
    return res.data;
};

export const getDraft = async (id: string): Promise<WeeklyMailDraft> => {
    const res = await axios.get(`${BASE}/drafts/${id}`, { headers: authHeaders() });
    return res.data;
};

export const updateDraft = async (
    id: string,
    payload: Partial<WeeklyMailDraft>
): Promise<WeeklyMailDraft> => {
    const res = await axios.patch(`${BASE}/drafts/${id}`, payload, { headers: authHeaders() });
    return res.data;
};

/**
 * Répond 409 `RESEND_BLOCKED` si l'établissement a déjà été envoyé à ces destinataires depuis
 * moins de 6 mois. `force` passe outre — c'est un choix explicite, jamais un défaut.
 */
export const addDraftCard = async (
    id: string,
    establishmentId: string,
    force = false
): Promise<WeeklyMailDraft> => {
    const res = await axios.post(
        `${BASE}/drafts/${id}/cards`,
        { establishmentId, force },
        { headers: authHeaders() }
    );
    return res.data;
};

export const sendDraftNow = async (
    id: string
): Promise<{ sent: number; failed: number; draft: WeeklyMailDraft }> => {
    const res = await axios.post(`${BASE}/drafts/${id}/send`, {}, { headers: authHeaders() });
    return res.data;
};

export const sendDraftTest = async (id: string, email: string): Promise<{ sent: number; failed: number }> => {
    const res = await axios.post(`${BASE}/drafts/${id}/test`, { email }, { headers: authHeaders() });
    return res.data;
};

export const cancelDraft = async (id: string): Promise<{ ok: boolean }> => {
    const res = await axios.delete(`${BASE}/drafts/${id}`, { headers: authHeaders() });
    return res.data;
};

/**
 * Régénère CET envoi avec de nouveaux établissements : ceux déjà présents (hors épinglés) sont
 * mis de côté, et ne reviennent que si le mail tombait sinon sous son minimum.
 */
export const regenerateDraft = async (id: string): Promise<WeeklyMailDraft> => {
    const res = await axios.post(`${BASE}/drafts/${id}/regenerate`, {}, { headers: authHeaders() });
    return res.data;
};

/** Ajoute un établissement choisi comme les autres : mêmes critères, même ordre de remplissage. */
export const addAutoCard = async (id: string): Promise<WeeklyMailDraft> => {
    const res = await axios.post(`${BASE}/drafts/${id}/auto-card`, {}, { headers: authHeaders() });
    return res.data;
};

export interface WeeklyDraftTypology {
    label: string;
    description: string;
    defining: string[];
    count: number;
    searchPrompt: string;
    /** établissements du mail qui composent ce profil */
    members: string[];
}

/** Typologies dégagées des établissements actuellement dans le mail (rien n'est enregistré). */
export const getDraftTypologies = async (
    id: string
): Promise<{ summary: string; profiles: WeeklyDraftTypology[] }> => {
    const res = await axios.post(`${BASE}/drafts/${id}/typologies`, {}, { headers: authHeaders() });
    return res.data;
};

export interface WeeklyPressItem {
    text: string;
    meta: string;
    url: string | null;
}

export interface WeeklyZoneNews {
    _id: string;
    title: string;
    link: string | null;
    published: string | null;
    city: string | null;
    source: string | null;
    status: "approved" | "pending" | "rejected";
    category: string;
    /** déjà mise en forme comme une ligne du bloc presse */
    press: WeeklyPressItem;
}

/** Toutes les actualités de la zone du profil, de la plus récente à la plus ancienne. */
export const getDraftNews = async (
    id: string,
    params: { offset?: number; limit?: number; status?: "approved" | "all"; q?: string } = {}
): Promise<{ zone: string; total: number; items: WeeklyZoneNews[] }> => {
    const res = await axios.get(`${BASE}/drafts/${id}/news`, { params, headers: authHeaders() });
    return res.data;
};

// ─── Calendrier & envois un par un ────────────────────────────────────────────

/**
 * `prepared` brief prêt · `planned` pas encore préparé · `skipped` sauté · `cancelled` annulé ·
 * `failed` en échec. Ce qui est parti est dans `sends`, pas ici.
 */
export type WeeklyCalendarStatus = "prepared" | "planned" | "skipped" | "cancelled" | "failed";

export interface WeeklyCalendarCard {
    establishmentId: string;
    name: string;
    slot: WeeklySlotKind;
    slotLabel: string | null;
    reason: string | null;
    reasonTag: string | null;
    owner: string | null;
    rating: string | null;
    subtitle: string | null;
}

export interface WeeklyCalendarItem {
    /** heure de départ actuelle — c'est aussi ce qui identifie l'envoi */
    at: string;
    status: WeeklyCalendarStatus;
    kind: "series" | "oneoff";
    /** déplacé par rapport au rythme hebdomadaire */
    moved: boolean;
    /** le créneau du rythme qu'il remplace ; null pour un envoi ponctuel */
    originalAt: string | null;
    overrideId: string | null;
    note: string | null;
    draftId: string | null;
    subject: string | null;
    edited: boolean;
    error: string | null;
    recipients: string[];
    cards: WeeklyCalendarCard[];
}

export interface WeeklyMailCalendar {
    from: string;
    to: string;
    sends: WeeklyMailSendEntry[];
    upcoming: WeeklyCalendarItem[];
    /** profil en pause : rien ne part tant qu'il n'est pas réactivé */
    paused: boolean;
    /** envois de la série restants ; null = illimité */
    remaining: number | null;
    previewLeadDays: number;
}

export const getProfileCalendar = async (
    profileId: string,
    from: Date,
    to: Date
): Promise<WeeklyMailCalendar> => {
    const res = await axios.get(`${BASE}/profiles/${profileId}/calendar`, {
        params: { from: from.toISOString(), to: to.toISOString() },
        headers: authHeaders(),
    });
    return res.data;
};

/** Ajoute un envoi ponctuel, hors rythme hebdomadaire. */
export const createOccurrence = async (
    profileId: string,
    at: Date,
    note?: string
): Promise<{ ok: boolean; at: string }> => {
    const res = await axios.post(
        `${BASE}/profiles/${profileId}/occurrences`,
        { at: at.toISOString(), note },
        { headers: authHeaders() }
    );
    return res.data;
};

/** Agit sur UN envoi du calendrier — les autres ne bougent pas. */
export const updateOccurrence = async (
    profileId: string,
    payload: { at: string; action: "move" | "skip" | "restore" | "delete"; to?: string }
): Promise<{ ok: boolean }> => {
    const res = await axios.patch(`${BASE}/profiles/${profileId}/occurrences`, payload, { headers: authHeaders() });
    return res.data;
};

/** Prépare dès maintenant le brief d'un envoi à venir. */
export const prepareOccurrence = async (profileId: string, at: string): Promise<WeeklyMailDraft> => {
    const res = await axios.post(`${BASE}/profiles/${profileId}/occurrences/prepare`, { at }, { headers: authHeaders() });
    return res.data;
};

// ─── Recipient reviews collected from the mail ────────────────────────────────

export interface WeeklyMailFeedbackItem {
    _id: string;
    establishmentId: string;
    establishmentName?: string;
    draftId?: string;
    recipientEmail?: string;
    vote: "relevant" | "not_relevant";
    comment?: string;
    createdAt: string;
}

export const listFeedback = async (params?: {
    draftId?: string;
    establishmentId?: string;
}): Promise<WeeklyMailFeedbackItem[]> => {
    const res = await axios.get(`${BASE}/feedback`, { params, headers: authHeaders() });
    return res.data;
};

export interface WeeklySentSignal {
    type: string;
    label: string;
    date: string | null;
}

export interface WeeklyMailProfileFeedbackItem {
    _id: string;
    establishmentId: string;
    establishmentName: string;
    siret: string | null;
    siren: string | null;
    city: string | null;
    postcode: string | null;
    vote: "relevant" | "not_relevant";
    comment: string | null;
    commentedAt: string | null;
    recipientEmail: string | null;
    /** quand l'avis a été donné */
    respondedAt: string;
    draftId: string;
    /** quand l'établissement est parti par mail */
    sentAt: string | null;
    scheduledFor: string | null;
    /** pourquoi il était dans le brief */
    slot: WeeklySlotKind | null;
    slotLabel: string | null;
    reason: string | null;
    reasonTag: string | null;
    /** ce qu'il était au moment de l'envoi, pas aujourd'hui */
    signals: WeeklySentSignal[];
    categories: string[];
    subCategories: string[];
    owner: string | null;
    rating: string | null;
    phone: string | null;
    email: string | null;
}

export interface WeeklyMailProfileFeedback {
    items: WeeklyMailProfileFeedbackItem[];
    counts: { total: number; relevant: number; notRelevant: number; withComment: number };
}

/** Tous les avis reçus pour un profil, avec la date d'envoi de l'établissement. */
export const getProfileFeedback = async (profileId: string): Promise<WeeklyMailProfileFeedback> => {
    const res = await axios.get(`${BASE}/profiles/${profileId}/feedback`, { headers: authHeaders() });
    return res.data;
};

/** Journalise un envoi fait à la main (briefing copié/collé hors profil). */
export const markWeeklyMailSent = async (params: {
    establishmentIds: string[];
    title?: string;
    dateFrom?: string;
    dateTo?: string;
    slots?: Record<string, string>;
    /** destinataires réels — sans eux l'envoi est journalisé mais ne bloque personne 6 mois */
    recipientEmails?: string[];
}): Promise<{ ok: boolean; count: number; recorded?: boolean }> => {
    const res = await axios.post(`${BASE}/mark-sent`, params, { headers: authHeaders() });
    return res.data;
};


// ─── Historique réel des envois d'un profil ───────────────────────────────────

export interface WeeklySentEstablishment {
    establishmentId: string;
    name: string;
    siret: string | null;
    city: string | null;
    slot: WeeklySlotKind;
    slotLabel: string;
    reason: string;
    reasonTag: string | null;
    signals: WeeklySentSignal[];
    owner: string | null;
    rating: string | null;
    /** avis du destinataire sur cet établissement, pour cet envoi */
    vote: "relevant" | "not_relevant" | null;
    comment: string | null;
    /** tous les avis reçus pour cet établissement dans cet envoi, un par destinataire */
    reviews?: {
        vote: "relevant" | "not_relevant";
        comment: string | null;
        recipientEmail: string | null;
        at: string | null;
    }[];
}

export interface WeeklyMailSendEntry {
    key: string;
    draftId: string | null;
    sentAt: string;
    recipients: string[];
    establishments: WeeklySentEstablishment[];
}

export interface WeeklyMailHistory {
    sends: WeeklyMailSendEntry[];
    counts: { sends: number; establishments: number; uniqueEstablishments: number };
    /** durée du blocage de renvoi, en jours */
    blockDays: number;
}

/** Ce qui est réellement parti — écrit à la livraison, jamais à la préparation. */
export const getProfileHistory = async (profileId: string): Promise<WeeklyMailHistory> => {
    const res = await axios.get(`${BASE}/profiles/${profileId}/history`, { headers: authHeaders() });
    return res.data;
};

// ─── Recherche par typologie client ───────────────────────────────────────────

export interface TypologySearchResult {
    prompt: string;
    filters: Record<string, any>;
    explanation: string;
    /** ce que l'assistant n'a pas su exprimer */
    unsupported: string[];
    /** ce qu'il a exprimé mais que cette recherche ne sait pas appliquer */
    ignored: string[];
    establishments: WeeklyEstablishment[];
    totalMatched: number;
    /** trouvés puis retenus par le blocage de 6 mois */
    blocked: number;
    blockDays: number;
    /** zone du profil appliquée, quand la description n'en nommait aucune */
    zoneApplied: string | null;
    /** critères abandonnés pour atteindre le minimum de résultats, dans l'ordre */
    relaxed: string[];
}

export const searchByTypology = async (params: {
    typologyId?: string;
    prompt?: string;
    draftId?: string;
    profileId?: string;
    limit?: number;
}): Promise<TypologySearchResult> => {
    const res = await axios.post(`${BASE}/typology-search`, params, { headers: authHeaders() });
    return res.data;
};
